// src/components/CounterpartyEditSheet.jsx
//
// Модалка редактирования контрагента: поставщика или оптового клиента.
// Поддерживает подтягивание данных из другого магазина Алсатуу по shopId.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { counterparties as cpApi } from '../api';
import Icon from './Icon';
import SafeInput from './SafeInput';
import ScanButton from './ScanButton';

// Извлекает 8-символьный ID магазина из отсканированного QR.
// Поддерживает: чек-QR ({"shopId":"...","token":"..."}), ссылку с ID,
// либо голый код. Если ничего не подошло — вернёт очищенную строку как есть.
function extractShopId(raw) {
  if (!raw) return '';
  const s = String(raw).trim();
  // 1) JSON-payload (чек магазина или карта)
  try {
    const obj = JSON.parse(s);
    if (obj && (obj.shopId || obj.shop)) return String(obj.shopId || obj.shop).trim().toUpperCase();
  } catch (e) {}
  // 2) URL со ссылкой на магазин (.../shop/ID, ?shop=ID, .../r/ID)
  const m = s.match(/(?:shop[/=]|\/r\/)([A-Za-z0-9]{4,})/);
  if (m) return m[1].toUpperCase();
  // 3) Голый код
  return s.replace(/[^A-Za-z0-9]/g, '').toUpperCase();
}

const API_HOST = (() => {
  try {
    const u = new URL(process.env.REACT_APP_API_URL || 'https://api.alsatuu.biz/api');
    return `${u.protocol}//${u.host}`;
  } catch { return 'https://api.alsatuu.biz'; }
})();
function fullUrl(u) {
  if (!u) return '';
  if (u.startsWith('http')) return u;
  return API_HOST + u;
}

export default function CounterpartyEditSheet({ open, counterparty, onClose, onSaved }) {
  const { t } = useTranslation();

  const isNew = !counterparty?.id;

  const [name, setName]                       = useState('');
  const [phone, setPhone]                     = useState('');
  const [whatsapp, setWhatsapp]               = useState('');
  const [email, setEmail]                     = useState('');
  const [address, setAddress]                 = useState('');
  const [notes, setNotes]                     = useState('');
  const [isSupplier, setIsSupplier]           = useState(false);
  const [isWholesale, setIsWholesale]         = useState(false);

  const [linkedShopId, setLinkedShopId]       = useState('');
  const [linkedShop, setLinkedShop]           = useState(null);   // { id, name, photo, whatsapp }
  const [lookupBusy, setLookupBusy]           = useState(false);
  const [lookupError, setLookupError]         = useState('');

  const [saving, setSaving]                   = useState(false);
  const [error, setError]                     = useState('');

  // Сброс при открытии
  useEffect(() => {
    if (!open) return;
    const c = counterparty || {};
    setName(c.name || '');
    setPhone(c.phone || '');
    setWhatsapp(c.whatsapp || '');
    setEmail(c.email || '');
    setAddress(c.address || '');
    setNotes(c.notes || '');
    setIsSupplier(c.isSupplier !== false);          // у новых по умолчанию = поставщик
    setIsWholesale(!!c.isWholesaleCustomer);
    setLinkedShopId(c.linkedShopId || '');
    setLinkedShop(c.linkedShop || null);
    setLookupError('');
    setError('');
    setSaving(false);
  }, [open, counterparty]);

  if (!open) return null;

  const lookupShop = async (explicitId) => {
    const id = String(explicitId != null ? explicitId : linkedShopId).trim().toUpperCase();
    if (!id) return;
    if (explicitId != null) setLinkedShopId(id);
    setLookupBusy(true);
    setLookupError('');
    try {
      const r = await cpApi.lookupShop(id);
      setLinkedShop(r);
      // Если поля пустые — предзаполнить
      if (!name)     setName(r.name || '');
      if (!whatsapp) setWhatsapp(r.whatsapp || '');
      if (!address && r.country) setAddress(r.country);
    } catch (e) {
      setLookupShop(null);
      if (e.serverError === 'shop_not_found')   setLookupError(t('counterparties.errShopNotFound'));
      else if (e.serverError === 'cant_link_self') setLookupError(t('counterparties.errCantLinkSelf'));
      else setLookupError(e.message || t('common.error'));
      setLinkedShop(null);
    } finally {
      setLookupBusy(false);
    }
  };

  const clearLinked = () => {
    setLinkedShopId('');
    setLinkedShop(null);
    setLookupError('');
  };

  const save = async () => {
    setError('');
    if (!name.trim()) {
      setError(t('counterparties.errNameRequired'));
      return;
    }
    if (!isSupplier && !isWholesale) {
      setError(t('counterparties.errTypeRequired'));
      return;
    }

    setSaving(true);
    try {
      const data = {
        name: name.trim(),
        phone: phone.trim() || null,
        whatsapp: whatsapp.trim() || null,
        email: email.trim() || null,
        address: address.trim() || null,
        notes: notes.trim() || null,
        isSupplier,
        isWholesaleCustomer: isWholesale,
        linkedShopId: linkedShop?.shopId || linkedShop?.id || null,
      };
      let result;
      if (isNew) {
        result = await cpApi.create(data);
      } else {
        result = await cpApi.update(counterparty.id, data);
      }
      onSaved && onSaved(result, isNew);
    } catch (e) {
      setError(e.message || t('common.error'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex', alignItems: 'flex-end', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'var(--bg)',
          width: '100%', maxWidth: 520,
          maxHeight: '92vh', overflow: 'auto',
          borderRadius: 'var(--r-lg) var(--r-lg) 0 0',
          padding: 20,
        }}
      >
        {/* Шапка */}
        <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
          <h2 style={{ fontSize: 18, fontWeight: 700, flex: 1 }}>
            {isNew ? t('counterparties.addNew') : t('counterparties.editTitle')}
          </h2>
          <button onClick={onClose} style={{
            background: 'none', border: 'none', cursor: 'pointer',
            padding: 8, color: 'var(--fg-muted)',
          }}>
            <Icon name="x" size={20} />
          </button>
        </div>

        {/* Тип контрагента */}
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('counterparties.typeLabel')}
          </p>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isSupplier}
              onChange={(e) => setIsSupplier(e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t('counterparties.typeSupplier')}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{t('counterparties.typeSupplierHint')}</div>
            </div>
          </label>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 6, cursor: 'pointer' }}>
            <input
              type="checkbox"
              checked={isWholesale}
              onChange={(e) => setIsWholesale(e.target.checked)}
            />
            <div>
              <div style={{ fontSize: 14, fontWeight: 600 }}>{t('counterparties.typeWholesale')}</div>
              <div style={{ fontSize: 11, color: 'var(--fg-faint)' }}>{t('counterparties.typeWholesaleHint')}</div>
            </div>
          </label>
        </div>

        {/* Lookup по shop ID */}
        <div className="card" style={{ marginBottom: 12 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: 'var(--fg-muted)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
            {t('counterparties.linkedShopLabel')}
          </p>
          <p style={{ fontSize: 11, color: 'var(--fg-faint)', marginBottom: 10 }}>
            {t('counterparties.linkedShopHint')}
          </p>

          {linkedShop ? (
            <div style={{
              padding: 10,
              background: 'var(--brand-primary-soft)',
              borderRadius: 'var(--r-sm)',
              display: 'flex', alignItems: 'center', gap: 10,
            }}>
              {linkedShop.photo && (
                <img
                  src={fullUrl(linkedShop.photo)}
                  alt=""
                  style={{ width: 36, height: 36, borderRadius: 'var(--r-sm)', objectFit: 'cover' }}
                />
              )}
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--brand-primary)' }}>{linkedShop.name || linkedShop.shopId}</div>
                <div style={{ fontSize: 11, color: 'var(--fg-muted)', fontFamily: 'var(--font-mono)' }}>{linkedShop.shopId || linkedShop.id}</div>
              </div>
              <button onClick={clearLinked} style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--fg-muted)', padding: 4,
              }}>
                <Icon name="x" size={16} />
              </button>
            </div>
          ) : (
            <>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <SafeInput
                  value={linkedShopId}
                  onChange={(e) => setLinkedShopId(e.target.value.toUpperCase())}
                  placeholder="ABCD1234"
                  style={{
                    width: '100%',
                    fontFamily: 'var(--font-mono)',
                    letterSpacing: '0.15em',
                    fontSize: 18,
                    fontWeight: 700,
                    textAlign: 'center',
                    textTransform: 'uppercase',
                  }}
                />
                <div style={{ display: 'flex', gap: 8 }}>
                  <ScanButton
                    variant="button"
                    raw
                    disabled={lookupBusy}
                    label={t('common.scan')}
                    style={{ flex: 1, justifyContent: 'center' }}
                    onScan={(code) => {
                      const id = extractShopId(code);
                      if (id) lookupShop(id);
                    }}
                  />
                  <button
                    onClick={() => lookupShop()}
                    disabled={lookupBusy || !linkedShopId.trim()}
                    className="btn btn-primary"
                    style={{ flex: 1 }}
                  >
                    {lookupBusy ? '…' : t('counterparties.lookupBtn')}
                  </button>
                </div>
              </div>
              {lookupError && (
                <p style={{ fontSize: 11, color: 'var(--danger)', marginTop: 6 }}>{lookupError}</p>
              )}
            </>
          )}
        </div>

        {/* Основные данные */}
        <div className="input-group">
          <label>{t('counterparties.name')} *</label>
          <SafeInput
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('counterparties.namePh')}
          />
        </div>

        <div className="input-group">
          <label>{t('counterparties.phone')}</label>
          <SafeInput
            type="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="+996 555 12 34 56"
          />
        </div>

        <div className="input-group">
          <label>{t('counterparties.whatsapp')}</label>
          <SafeInput
            type="tel"
            value={whatsapp}
            onChange={(e) => setWhatsapp(e.target.value)}
            placeholder="996555123456"
          />
        </div>

        <div className="input-group">
          <label>{t('counterparties.email')}</label>
          <SafeInput
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="email@example.com"
          />
        </div>

        <div className="input-group">
          <label>{t('counterparties.address')}</label>
          <SafeInput
            value={address}
            onChange={(e) => setAddress(e.target.value)}
            placeholder={t('counterparties.addressPh')}
          />
        </div>

        <div className="input-group">
          <label>{t('counterparties.notes')}</label>
          <textarea
            className="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder={t('counterparties.notesPh')}
            rows={3}
            autoCorrect="off"
            autoCapitalize="off"
            spellCheck="false"
          />
        </div>

        {error && (
          <div style={{
            padding: 10, marginBottom: 10,
            background: 'var(--danger-soft)',
            color: 'var(--danger)',
            borderRadius: 'var(--r-sm)',
            fontSize: 13,
          }}>{error}</div>
        )}

        <button
          onClick={save}
          disabled={saving}
          className="btn btn-primary btn-lg"
          style={{ width: '100%', marginTop: 8 }}
        >
          {saving ? t('common.saving') : (isNew ? t('common.create') : t('common.save'))}
        </button>

        <button
          onClick={onClose}
          disabled={saving}
          className="btn btn-ghost"
          style={{ width: '100%', marginTop: 6 }}
        >
          {t('common.cancel')}
        </button>
      </div>
    </div>
  );
}
